import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AiAgent } from '@azure/models';
import { environment } from 'environments/environment';

/**
 * Mantenimiento de agentes de IA. Se direccionan por nombre y no por el id de
 * Mongo: es lo que resuelve el runtime y lo que guardan sus padres.
 */
@Injectable({ providedIn: 'root' })
export class AiAgentService {
  private readonly url = `${environment.azureApiUrl}/api/ai-agents`;

  constructor(private _http: HttpClient) {}

  getAll() {
    return this._http.get<AiAgent[]>(this.url).pipe(
      map((resp) => resp as AiAgent[]),
      catchError((e) => throwError(e))
    );
  }

  /** Solo los activos: para los selectores. Las tablas usan getAll(). */
  getActive() {
    return this._http.get<AiAgent[]>(`${this.url}/active`).pipe(
      map((resp) => resp as AiAgent[]),
      catchError((e) => throwError(e))
    );
  }

  getByName(name: string) {
    return this._http.get<AiAgent>(`${this.url}/${name}`).pipe(
      map((resp) => resp as AiAgent),
      catchError((e) => throwError(e))
    );
  }

  /** Agentes que tienen a este como subagente; es lo que impide borrarlo. */
  getParents(name: string) {
    return this._http.get<string[]>(`${this.url}/${name}/parents`).pipe(
      map((resp) => resp as string[]),
      catchError((e) => throwError(e))
    );
  }

  create(agent: AiAgent) {
    return this._http.post<AiAgent>(this.url, agent).pipe(
      map((resp) => resp as AiAgent),
      catchError((e) => throwError(e))
    );
  }

  /** El nombre del cuerpo se ignora en el servidor: se edita el de la ruta. */
  update(name: string, agent: AiAgent) {
    return this._http.put<AiAgent>(`${this.url}/${name}`, agent).pipe(
      map((resp) => resp as AiAgent),
      catchError((e) => throwError(e))
    );
  }

  /**
   * Lo quita de la lista de subagentes de sus padres; el agente no se borra.
   * Sin `parent` lo saca de todos. Devuelve de cuáles se lo quitó.
   */
  detach(name: string, parent?: string) {
    const params = parent ? new HttpParams().set('parent', parent) : undefined;

    return this._http.delete<string[]>(`${this.url}/${name}/parents`, { params }).pipe(
      map((resp) => resp as string[]),
      catchError((e) => throwError(e))
    );
  }

  /** Ya no se borran: se desactivan y se pueden volver a activar. */
  enable(name: string) {
    return this._http.put<AiAgent>(`${this.url}/${name}/enable`, {}).pipe(
      map((resp) => resp as AiAgent),
      catchError((e) => throwError(e))
    );
  }

  disable(name: string) {
    return this._http.put<AiAgent>(`${this.url}/${name}/disable`, {}).pipe(
      map((resp) => resp as AiAgent),
      catchError((e) => throwError(e))
    );
  }
}
